import { PopoverTrigger } from "@hopper-ui/components";
import { tenantInfoQueryOptions } from "../../../api/routes/tenant.ts";
import { useGlobalShortcut } from "@sg-protect/components";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useTrackModalState } from "../../../tracking/useTrackEvent.ts";
import { useCanQueryTenantData } from "../../../useGovernanceTenantId.ts";
import { CommandPalettePopover } from "./CommandPalettePopover.tsx";
import { CommandPaletteTrigger } from "./CommandPaletteTrigger.tsx";
import {
    type ProtectSearchProviderOptions,
    SearchProviderBoundary,
    useRegisteredSearchProviders
} from "./registerProtectSearchProvider.tsx";
import { useDebouncedSearchQuery } from "./useDebouncedSearchQuery.ts";
import { useRecordRecentlyViewed } from "./useRecordRecentlyViewed.ts";

const SHORTCUT = "Mod+K";

const MODAL_NAME = "Global Search Bar";

const KEYBOARD_SHORTCUT_TRIGGER = "Keyboard Shortcut";

const SEARCH_BAR_TRIGGER = "Search Bar";

interface RecentlyViewedRecorderProps {
    providers: ProtectSearchProviderOptions[];
}

// A component rather than a hook call in InnerCommandPalette, so the boundary around it can catch a provider's useVisitedEntry.
function RecentlyViewedRecorder({ providers }: RecentlyViewedRecorderProps) {
    useRecordRecentlyViewed(providers);

    return null;
}

function InnerCommandPalette() {
    const [isOpen, setIsOpen] = useState(false);
    const search = useDebouncedSearchQuery();
    const [trackModalOpened, trackModalClosed] = useTrackModalState();
    const providers = useRegisteredSearchProviders();

    const { data: tenant } = useQuery(tenantInfoQueryOptions());

    const isOpenRef = useRef(false);

    const openPalette = useCallback(
        (trigger: string) => {
            if (isOpenRef.current) {
                return;
            }

            isOpenRef.current = true;
            trackModalOpened(MODAL_NAME, trigger);
            setIsOpen(true);
        },
        [trackModalOpened]
    );

    const closePalette = useCallback(() => {
        if (!isOpenRef.current) {
            return;
        }

        isOpenRef.current = false;
        trackModalClosed(MODAL_NAME);
        setIsOpen(false);
    }, [trackModalClosed]);

    const openWithShortcut = useCallback(() => openPalette(KEYBOARD_SHORTCUT_TRIGGER), [openPalette]);

    useGlobalShortcut(SHORTCUT, openWithShortcut);

    const handleOpenChange = useCallback(
        (nextIsOpen: boolean) => {
            if (nextIsOpen) {
                openPalette(SEARCH_BAR_TRIGGER);
            } else {
                closePalette();
            }
        },
        [openPalette, closePalette]
    );

    return (
        <>
            <SearchProviderBoundary>
                <RecentlyViewedRecorder providers={providers} />
            </SearchProviderBoundary>
            <PopoverTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
                <CommandPaletteTrigger shortcut={SHORTCUT} tenantName={tenant?.displayName} query={search.query} />
                <CommandPalettePopover
                    isOpen={isOpen}
                    onOpenChange={handleOpenChange}
                    search={search}
                    providers={providers}
                />
            </PopoverTrigger>
        </>
    );
}

// Rendered in the header, above the gates: before consent its tenant requests 403, and the 403 handlers redirect to setup.
export function CommandPalette() {
    const canQueryTenantData = useCanQueryTenantData();

    if (!canQueryTenantData) {
        return null;
    }

    return <InnerCommandPalette />;
}
